import React from 'react';
import { Heart, Sparkles, Award, Palette, ShieldCheck, CheckCircle2 } from 'lucide-react';

interface MeetKyleeProps {
  onOpenCustomOrderModal: () => void;
}

export const MeetKylee: React.FC<MeetKyleeProps> = ({ onOpenCustomOrderModal }) => {
  return (
    <section id="about" className="py-16 md:py-24 bg-gradient-to-b from-[#FCFAF8] via-[#FFFDF9] to-[#FCFAF8] relative overflow-hidden">
      {/* Decorative Pastel Background Blobs */}
      <div className="absolute top-1/4 -left-20 w-80 h-80 rounded-full bg-[#FCE7F3]/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-20 w-80 h-80 rounded-full bg-[#EDE9FE]/50 blur-3xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#FEF08A]/70 text-[#854D0E] text-xs font-bold uppercase tracking-wider border border-[#FDE047]">
            <Heart className="w-3.5 h-3.5 fill-[#854D0E]" />
            <span>Meet The Artist & Founder</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#292524]">
            Gymnast by Day, <span className="gold-gradient-text">Nail Artist by Passion</span>
          </h2>
          <p className="text-[#57534E] text-base sm:text-lg">
            How a 14-year-old gymnast combined athletic discipline and creative flair to craft salon-quality press-on nails.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Visual Showcase Card */}
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Outer Golden Glow Border */}
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-[#D4AF37] via-[#F472B6] to-[#2DD4BF] opacity-30 blur-lg" />
              
              <div className="relative rounded-3xl bg-white p-6 sm:p-8 shadow-xl border border-[#F3C053]/30 space-y-6">
                
                {/* Artist Bio Avatar / Graphic with Gymnastics Silhouette */}
                <div className="relative w-full aspect-4/3 rounded-2xl bg-gradient-to-br from-[#FEF3C7] via-[#FCE7F3] to-[#EDE9FE] overflow-hidden flex flex-col items-center justify-center p-6 text-center border border-[#FDE68A]">
                  <div className="w-24 h-24 rounded-full bg-white/90 p-2 shadow-md mb-3 flex items-center justify-center">
                    <img
                      src="/logo.png"
                      alt="Kylee - Nailed It"
                      className="w-full h-full object-contain rounded-full"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/Nailed It Logo.png';
                      }}
                    />
                  </div>
                  <span className="font-serif text-2xl font-bold text-[#292524]">Kylee</span>
                  <span className="text-xs font-semibold text-[#B45309] tracking-wider uppercase bg-white/80 px-3 py-0.5 rounded-full mt-1">
                    14 Years Old · Level 8 Gymnast · Nail Artist
                  </span>
                  
                  {/* Floating badge */}
                  <div className="absolute top-3 right-3 bg-[#10B981] text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-xs flex items-center gap-1">
                    <span>Active Gymnast</span>
                  </div>
                </div>

                {/* Kylee's 3 Pillars */}
                <div className="space-y-3.5 pt-2">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-[#FAF5EE]/80 border border-[#EADFCF]">
                    <div className="p-2 rounded-lg bg-white text-[#D4AF37] shadow-xs">
                      <Award className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#292524]">Gymnastics Discipline & Precision</h4>
                      <p className="text-[11px] text-[#78716C]">Hours on the balance beam trained Kylee with microscopic hand steadiness and attention to detail.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-[#FAF5EE]/80 border border-[#EADFCF]">
                    <div className="p-2 rounded-lg bg-white text-[#EC4899] shadow-xs">
                      <Palette className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#292524]">100% Hand-Painted Gel Art</h4>
                      <p className="text-[11px] text-[#78716C]">No cheap plastic factory prints. Every swirl, star, and gold chrome contour is drawn by hand.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-[#FAF5EE]/80 border border-[#EADFCF]">
                    <div className="p-2 rounded-lg bg-white text-[#8B5CF6] shadow-xs">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#292524]">Gymnast-Tested Durability</h4>
                      <p className="text-[11px] text-[#78716C]">Multi-layer salon builder gel ensures your nails won't snap, chip, or bend during everyday activities.</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Right Column: Kylee's Story */}
          <div className="lg:col-span-7 space-y-6">
            
            <div className="space-y-4">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#292524]">
                "I believe everyone deserves salon-quality nails that don’t cost a fortune or ruin natural nails."
              </h3>
              
              <div className="space-y-3.5 text-[#57534E] text-base leading-relaxed">
                <p>
                  Hi, I’m <strong>Kylee</strong>! I’m 14 years old, and when I’m not training flips, routines, and handstands in the gymnastics gym, you’ll almost always find me at my art desk with my gel brushes, metallic chrome powders, and nail swatches.
                </p>
                <p>
                  As an athlete, regular salon acrylics always damaged my natural nail beds and cost way too much. I started experimenting with hand-painting custom press-on sets for my teammates so we could wear gorgeous, sparkly nails to banquets, school dances, and weekends—and easily take them off before training.
                </p>
                <p>
                  Every single set in my shop is made by me with love, high-grade Japanese gel polish, and a reinforced apex structure so they look just like expensive salon extensions!
                </p>
              </div>
            </div>

            {/* Key Quality Standards List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {[
                'Handcrafted individually with love',
                'Salon-grade builder gel (no flimsy plastic)',
                '100% reusable & gentle on natural nails',
                'Custom millimeter sizing available',
                'All sets priced fairly at $30',
                'Free application kit included with every set',
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm font-medium text-[#292524]">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Custom Request Banner */}
            <div className="p-5 rounded-2xl bg-gradient-to-r from-[#FEF08A]/40 via-[#FCE7F3]/40 to-[#EDE9FE]/40 border border-[#F3C053]/40 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-sm text-[#292524]">Want a custom design to match your team or event?</h4>
                <p className="text-xs text-[#78716C]">Send your inspiration photos or colors directly to Kylee!</p>
              </div>
              <button
                onClick={onOpenCustomOrderModal}
                className="shrink-0 px-5 py-2.5 rounded-xl font-bold text-xs text-white gold-shimmer-btn shadow-md hover:scale-102 transition-transform"
              >
                Request Custom Set
              </button>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
